<template>
  <EmdPanel>
    <EmdPanelSection :title="$t('recipients.create.transfer_settings.title')">
      <EmdFormGroup
        :label="$t('recipients.create.transfer_settings.transfer_enabled')"
      >
        <EmdSwitcher
          :label="
            transfer_settings.transfer_enabled
              ? $t('recipients.create.active')
              : $t('recipients.create.inactive')
          "
          id="transfer_enabled"
          v-model="transfer_settings.transfer_enabled"
        />
      </EmdFormGroup>

      <EmdFormGroup
        :label="$t('recipients.create.transfer_settings.interval.label')"
        :required="transfer_settings.transfer_enabled"
      >
        <div class="eu-mt_2">
          <EmdRadio
            :label="$t('recipients.create.transfer_settings.interval.daily')"
            id="transfer_interval_daily"
            value="Daily"
            v-model="transfer_settings.transfer_interval"
            @change="() => (transfer_settings.transfer_day = 0)"
            class="eu-mr_4 eu-mb_2"
          />

          <EmdRadio
            :label="$t('recipients.create.transfer_settings.interval.weekly')"
            id="transfer_interval_weekly"
            value="Weekly"
            v-model="transfer_settings.transfer_interval"
            @change="() => (transfer_settings.transfer_day = 0)"
            class="eu-mr_4 eu-mb_2"
          />

          <EmdRadio
            :label="$t('recipients.create.transfer_settings.interval.monthly')"
            id="transfer_interval_monthly"
            value="Monthly"
            v-model="transfer_settings.transfer_interval"
            @change="() => (transfer_settings.transfer_day = null)"
          />
        </div>
      </EmdFormGroup>

      <EmdFormGroup
        v-if="showCustomTransferDay"
        :required="transfer_settings.transfer_enabled"
        :state="
          getState(
            $v.transfer_settings.transfer_day,
            transfer_settings.transfer_enabled
          )
        "
        :feedbacks="
          feedbacks(
            $v.transfer_settings.transfer_day,
            transfer_settings.transfer_enabled
          )
        "
      >
        <label class="emd-label _required">
          {{ $t('recipients.create.transfer_settings.transfer_day') }}

          <emd-tippy
            data-template="transfer_settings_info"
            id="transfer_settings_info"
            class="eu-ml_1"
            :type="'html'"
            position="bottom"
          >
            <i
              :class="'emd-text _color_subtle'"
              class="uil uil-info-circle"
            />
            <emd-tippy-template-HTML id="transfer_settings_info">
              <div style="text-transform: initial; letter-spacing: 0">
                <p>{{ $t('recipients.create.transfer_settings_info') }}</p>
              </div>
            </emd-tippy-template-HTML>
          </emd-tippy>
        </label>

        <EmdInput
          name="transfer_day"
          type="number"
          @input="$v.transfer_settings.transfer_day.$touch"
          v-model.number="transfer_settings.transfer_day"
          v-if="showDayTransferInput"
        />

        <EmdSelect
          v-model="transfer_settings.transfer_day"
          @change="$v.transfer_settings.transfer_day.$touch"
          v-if="showWeekDaySelect"
        >
          <option :value="1">{{ $t('week_days.monday') }}</option>
          <option :value="2">{{ $t('week_days.tuesday') }}</option>
          <option :value="3">{{ $t('week_days.wednesday') }}</option>
          <option :value="4">{{ $t('week_days.thursday') }}</option>
          <option :value="5">{{ $t('week_days.friday') }}</option>
        </EmdSelect>
      </EmdFormGroup>
    </EmdPanelSection>
  </EmdPanel>
</template>

<script>
import {
  EmdPanel,
  EmdPanelSection,
  EmdFormGroup,
  EmdInput,
  EmdSelect,
  EmdRadio,
  EmdSwitcher,
  EmdTippy,
  EmdTippyTemplateHTML
} from 'emerald-vue/src/components'

import { getState, feedbacks } from '@/utils/form.utils'
import {
  isTransferIntervalTypeDaily,
  isTransferIntervalTypeMonthly,
  isTransferIntervalTypeWeekly
} from '@/enums/transfer-settings.enum'

import { required } from 'vuelidate/lib/validators'

export default {
  name: 'TransferSettings',
  components: {
    EmdPanel,
    EmdPanelSection,
    EmdFormGroup,
    EmdInput,
    EmdSelect,
    EmdRadio,
    EmdSwitcher,
    EmdTippy,
    EmdTippyTemplateHTML
  },
  data: () => ({
    getState,
    feedbacks
  }),
  props: {
    transfer_settings: {
      type: Object,
      required: true
    },
    transferSettingsIsInvalid: Boolean
  },
  validations () {
    const transferDayValidations = {
      daily: {},
      weekly: {
        lessThan: value => value <= 5,
        biggerThan: value => value >= 1
      },
      monthly: {
        lessThan: value => value <= 31,
        biggerThan: value => value >= 1
      }
    }

    const transferInterval =
      this.transfer_settings.transfer_interval?.toLowerCase() || 'daily'

    const transferSettings = {
      transfer_day: transferDayValidations[transferInterval],
      transfer_interval: {
        required
      }
    }

    return {
      transfer_settings: transferSettings
    }
  },
  computed: {
    showCustomTransferDay () {
      return (
        this.transfer_settings.transfer_interval &&
        !isTransferIntervalTypeDaily(this.transfer_settings.transfer_interval)
      )
    },
    showWeekDaySelect () {
      return isTransferIntervalTypeWeekly(
        this.transfer_settings.transfer_interval
      )
    },
    showDayTransferInput () {
      return isTransferIntervalTypeMonthly(
        this.transfer_settings.transfer_interval
      )
    }
  },
  watch: {
    transfer_settings: {
      handler () {
        this.$v.transfer_settings.$touch()

        this.$emit(
          'update:transferSettingsIsInvalid',
          this.$v.transfer_settings.$invalid
        )
      },
      deep: true
    }
  }
}
</script>
